import { DEFAULT_LOCALE, LOCALES, isLocale, type Locale } from "./locales";

const STORAGE_KEY = "sprinkledays:locale";

/**
 * Which of our languages a device is asking for.
 *
 * The device, not the country. A Thai speaker on holiday in Germany wants Thai,
 * and an English speaker living in Bangkok wants English; the browser's own
 * language list answers that and an IP address guesses at it. `navigator.
 * languages` is already in the reader's order of preference, so the first entry
 * that matches one of ours wins.
 *
 * Region subtags are dropped on purpose. `nl-BE` and `nl-NL` are both Dutch as
 * far as this site is concerned, and matching on the primary subtag is what
 * stops a Flemish reader being served English.
 */
export function detectLocale(
  languages: readonly string[] = [],
): Locale | null {
  for (const language of languages) {
    const primary = language.toLowerCase().split("-")[0];
    if (isLocale(primary)) return primary;
  }

  return null;
}

/** What the browser is asking for, guarded for the build-time render. */
export function deviceLocale(): Locale | null {
  if (typeof navigator === "undefined") return null;

  return detectLocale(
    navigator.languages?.length
      ? navigator.languages
      : [navigator.language].filter(Boolean),
  );
}

/**
 * A choice made by hand outranks the device, forever after.
 *
 * Without this, someone whose phone is set to Thai but who prefers reading the
 * English page would be moved back to Thai on every visit, and the switcher
 * would look broken rather than deliberate.
 */
export function readStoredLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private browsing refuses storage. The device language still answers.
  }
}

/**
 * The locale the app surface should speak.
 *
 * The private pages carry no locale in their URL, because they are noindex and
 * a second copy of every app route would buy nothing a crawler could use. They
 * follow the stored choice, then the device, then English.
 */
export function preferredLocale(): Locale {
  return readStoredLocale() ?? deviceLocale() ?? DEFAULT_LOCALE;
}

export { LOCALES, DEFAULT_LOCALE };
